<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="500px" persistent>
    <v-card>
      <v-card-title class="pb-2">
        <span class="headline">Download Records</span>
      </v-card-title>
      <v-card-text>
        <v-container class="pt-3 pb-2">
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="selectedRowCount"
                :items="rowCountOptions"
                label="Number of rows to download"
                outlined
                dense
                item-text="label"
                item-value="value"
                return-object
              ></v-select>
            </v-col>
            <v-col cols="12" class="py-1">
              <div class="text-caption mb-2">
                <v-icon small class="mr-1">mdi-information-outline</v-icon>
                <span v-if="selectedRowCount && selectedRowCount.value === 'all'">
                  Total available records: {{ totalRecords.toLocaleString() }}
                  <span class="ml-2 text--secondary">
                    (Will download all {{ totalRecords.toLocaleString() }} rows - Admin)
                  </span>
                </span>
                <span v-else>
                  Total available records: {{ cappedTotalRecords.toLocaleString() }}
                  <span v-if="totalRecords > cappedTotalRecords" class="ml-2 text--secondary">
                    ({{ totalRecords.toLocaleString() }} total, capped at {{ cappedTotalRecords.toLocaleString() }})
                  </span>
                  <span v-if="selectedRowCount && selectedRowCount.value > cappedTotalRecords" class="ml-2 text--secondary">
                    (Will download {{ cappedTotalRecords }} rows)
                  </span>
                </span>
              </div>
            </v-col>
          </v-row>
          <!-- Progress bar -->
          <v-row v-if="downloadingPages">
            <v-col cols="12" class="pt-3 pb-0">
              <div class="mb-2">
                <div class="d-flex justify-space-between align-center mb-1">
                  <span class="caption font-weight-medium">Download Progress</span>
                  <span class="caption">{{ downloadProgress.current }} / {{ downloadProgress.total }}</span>
                </div>
                <v-progress-linear
                  :value="downloadProgressPercentage"
                  color="primary"
                  height="24"
                  rounded
                  striped
                >
                  <template v-slot:default="{ value }">
                    <strong class="white--text">{{ Math.ceil(value) }}%</strong>
                  </template>
                </v-progress-linear>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="handleCancel">Cancel</v-btn>
        <v-btn 
          color="primary" 
          @click="handleDownload" 
          :disabled="!selectedRowCount || downloadingPages"
          :loading="downloadingPages"
        >
          <span v-if="selectedRowCount && selectedRowCount.value === 'all'">
            Download All ({{ totalRecords.toLocaleString() }} rows)
          </span>
          <span v-else>
            Download {{ selectedRowCount ? selectedRowCount.value : 0 }} Row(s)
          </span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { executeSearch, getCurrentUserProfile } from "./APIManager.js";
import { downloadPagesAsSingleCSV } from "./Utils.js";

export default {
  name: 'DownloadPagesDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    hitInfo: {
      type: Object,
      default: () => ({})
    },
    jsonQuery: {
      type: Object,
      default: () => ({})
    },
    hidePagination: {
      type: Boolean,
      default: false
    },
    currentPageRows: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      selectedRowCount: null,
      allRowCountOptions: [
        { label: '50 rows', value: 50 },
        { label: '100 rows', value: 100 },
        { label: '500 rows', value: 500 },
        { label: '1,000 rows', value: 1000 },
        { label: '2,000 rows', value: 2000 },
        { label: '3,000 rows', value: 3000 },
        { label: '4,000 rows', value: 4000 },
        { label: '5,000 rows', value: 5000 }
      ],
      downloadingPages: false,
      downloadProgress: {
        current: 0,
        total: 0
      },
      pageSize: 500, // Use 500 as the page size for API requests
      isAdmin: false,
      downloadCancelled: false
    };
  },
  computed: {
    totalRecords() {
      // If hide_pagination is true, only allow downloading the first page
      if (this.hidePagination) {
        return this.currentPageRows || 0;
      }
      return this.hitInfo.value || this.hitInfo.total || 0;
    },
    cappedTotalRecords() {
      // If hide_pagination is true, only allow downloading the first page
      if (this.hidePagination) {
        return this.currentPageRows || 0;
      }
      const totalRecords = this.hitInfo.value || this.hitInfo.total || 0;
      return Math.min(totalRecords, 5000);
    },
    rowCountOptions() {
      if (this.cappedTotalRecords === 0) {
        return [];
      }
      
      // Filter options to only show those that don't exceed the total available records
      let filtered = this.allRowCountOptions.filter(option => option.value <= this.cappedTotalRecords);
      
      // If no options match (total is less than minimum option), return just the total
      if (filtered.length === 0) {
        return [{
          label: `${this.cappedTotalRecords} row${this.cappedTotalRecords === 1 ? '' : 's'}`,
          value: this.cappedTotalRecords
        }];
      }
      
      // Sort by value to keep options in order
      filtered.sort((a, b) => a.value - b.value);
      
      // If total is less than 50, replace the smallest option with the exact total
      if (this.cappedTotalRecords < 50 && filtered.length > 0) {
        filtered[0] = {
          label: `${this.cappedTotalRecords} row${this.cappedTotalRecords === 1 ? '' : 's'}`,
          value: this.cappedTotalRecords
        };
      }
      
      // If total is less than 5000, replace the largest option with the exact total
      if (this.cappedTotalRecords < 5000 && filtered.length > 0) {
        const lastIndex = filtered.length - 1;
        filtered[lastIndex] = {
          label: `${this.cappedTotalRecords} row${this.cappedTotalRecords === 1 ? '' : 's'}`,
          value: this.cappedTotalRecords
        };
      }
      
      // Remove duplicates that might have been created
      const seen = new Set();
      filtered = filtered.filter(option => {
        if (seen.has(option.value)) {
          return false;
        }
        seen.add(option.value);
        return true;
      }).sort((a, b) => a.value - b.value);
      
      // Add "Download All" option at the end if user is admin and there are more records than the cap
      if (this.isAdmin && this.totalRecords > this.cappedTotalRecords) {
        filtered.push({
          label: `Download All (${this.totalRecords.toLocaleString()} rows) - Admin Only`,
          value: 'all'
        });
      }
      
      return filtered;
    },
    downloadProgressPercentage() {
      if (this.downloadProgress.total === 0) return 0;
      return Math.round((this.downloadProgress.current / this.downloadProgress.total) * 100);
    }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.initializeDialog();
      } else {
        // Dialog is closing - cancel any ongoing download
        this.cancelDownload();
      }
    },
    cappedTotalRecords(newTotal) {
      // Reset selection if it exceeds the new total
      if (this.selectedRowCount && this.selectedRowCount.value > newTotal) {
        this.selectedRowCount = null;
      }
    }
  },
  methods: {
    async checkAdminStatus() {
      try {
        const response = await getCurrentUserProfile();
        if (response.status === 'success') {
          this.isAdmin = response.data.tt_admin === true;
        }
      } catch (error) {
        // Silently fail - user is not admin
        this.isAdmin = false;
      }
    },
    initializeDialog() {
      this.selectedRowCount = null;
      this.downloadCancelled = false;
      this.checkAdminStatus();
    },
    cancelDownload() {
      if (this.downloadingPages) {
        this.downloadCancelled = true;
      }
    },
    handleCancel() {
      this.cancelDownload();
      this.$emit('input', false);
    },
    async handleDownload() {
      if (!this.selectedRowCount || !this.selectedRowCount.value) {
        return;
      }

      // Check if "Download All" was selected
      const isDownloadAll = this.selectedRowCount.value === 'all';
      
      // Verify admin status if trying to download all
      if (isDownloadAll && !this.isAdmin) {
        alert('You do not have permission to download all records. Admin access required.');
        return;
      }
      
      // Determine target row count - if "all", use totalRecords, otherwise use the selected value capped
      const targetRowCount = isDownloadAll 
        ? this.totalRecords 
        : Math.min(this.selectedRowCount.value, this.cappedTotalRecords);

      if (targetRowCount === 0) {
        alert('No rows available to download.');
        return;
      }

      this.downloadingPages = true;
      
      // If hide_pagination is true, only download the first page
      // If downloading all, calculate pages based on total records (or estimate if unknown)
      const pagesNeeded = this.hidePagination ? 1 : Math.ceil(targetRowCount / this.pageSize);
      this.downloadProgress = {
        current: 0,
        total: pagesNeeded || 1 // At least 1 to show progress
      };
      this.$emit('downloading', true);

      try {
        const query = JSON.parse(JSON.stringify(this.jsonQuery || this.$route.query));
        // When hide_pagination is true, use the actual number of rows on the page
        query.size = this.hidePagination ? this.currentPageRows : this.pageSize;

        const pagesData = [];
        let totalRowsDownloaded = 0;
        
        // Download pages until we have enough rows
        // If hide_pagination is true, only download page 1
        // If downloading all, continue until no more data is returned
        const maxPage = this.hidePagination ? 1 : (isDownloadAll ? Infinity : pagesNeeded);
        let page = 1;
        let hasMoreData = true;
        
        while (hasMoreData && (isDownloadAll || (page <= maxPage && totalRowsDownloaded < targetRowCount))) {
          // Check if download was cancelled
          if (this.downloadCancelled) {
            break;
          }
          
          try {
            const response = await executeSearch(query, page);
            if (response && response.data && Array.isArray(response.data) && response.data.length > 0) {
              // If downloading all, add all data; otherwise limit to target row count
              let dataToAdd;
              if (isDownloadAll) {
                dataToAdd = response.data;
              } else {
                const remainingRows = targetRowCount - totalRowsDownloaded;
                dataToAdd = response.data.slice(0, remainingRows);
              }
              
              if (dataToAdd.length > 0) {
                pagesData.push({
                  page: page,
                  data: dataToAdd
                });
                totalRowsDownloaded += dataToAdd.length;
              }
              
              // If we got fewer rows than the page size, we've reached the end
              if (response.data.length < this.pageSize) {
                hasMoreData = false;
              }
            } else {
              // No data returned, we've reached the end
              hasMoreData = false;
            }
            
            // Update progress
            this.downloadProgress.current++;
            
            // Update total pages if downloading all and we got a full page
            if (isDownloadAll && response && response.data && response.data.length === this.pageSize) {
              this.downloadProgress.total = this.downloadProgress.current + 1;
            }
            
            // Check if download was cancelled before delay
            if (this.downloadCancelled) {
              break;
            }
            
            // Small delay between requests to avoid overwhelming the server
            if (hasMoreData && (isDownloadAll || this.downloadProgress.current < pagesNeeded)) {
              await new Promise(resolve => setTimeout(resolve, 500));
              
              // Check again after delay
              if (this.downloadCancelled) {
                break;
              }
            }
            
            page++;
          } catch (error) {
            console.error(`Error fetching page ${page}:`, error);
            // Increment progress even on error
            this.downloadProgress.current++;
            hasMoreData = false; // Stop on error
          }
        }

        // Check if download was cancelled before processing results
        if (this.downloadCancelled) {
          // Download was cancelled, don't process or save the data
          return;
        }

        if (pagesData.length === 0) {
          alert('No data was retrieved. Please try again.');
        } else {
          // Always download as a single CSV file, combining all pages
          downloadPagesAsSingleCSV(pagesData);
          this.$emit('input', false);
        }
      } catch (error) {
        console.error('Error downloading rows:', error);
        alert('An error occurred while downloading. Please try again.');
      } finally {
        this.downloadingPages = false;
        this.downloadProgress = {
          current: 0,
          total: 0
        };
        this.downloadCancelled = false;
        this.$emit('downloading', false);
      }
    }
  }
};
</script>

<style scoped>
</style>

